'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { useAnalyticsContext } from '~/components/analytics-context';
import { usePathname } from '~/i18n/routing';
import { CountryCode } from '~/lib/atom/types/country';
import { CurrencyCode } from '~/lib/atom/types/currency';

interface Props {
  pageType: string;
  countryCode: CountryCode;
  currencyCode: CurrencyCode;
}

export const ClientPageView = ({ pageType, currencyCode, countryCode }: Props) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastUrl = useRef(`${pathname}?${searchParams.toString()}`);
  const eventSent = useRef(false);

  const analytics = useAnalyticsContext();

  useEffect(() => {
    const currentUrl = `${pathname}?${searchParams.toString()}`;

    // If the URL has changed, reset the event sent flag. It's
    // usually reset when the page reloads, but not when the URL
    // changes without a reload, such as switching SKUs on a PDP
    if (lastUrl.current !== currentUrl) {
      eventSent.current = false;
    }

    // If the event has already been sent, skip sending it again
    if (eventSent.current) {
      return;
    }

    // TODO: It'd be nice to only set these when they change and only set them individually.
    // So first load they all get set in one call and then subsequent calls only set the changed values.
    analytics.set({
      page_type: pageType,
      currency_code: currencyCode,
      country_code: countryCode,
    });

    analytics.pageView(currentUrl);

    lastUrl.current = currentUrl;
    eventSent.current = true;
  }, [searchParams, pathname, pageType, currencyCode, countryCode, analytics]);

  return null;
};
